import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { userService } from '../../services/user.service';
import { FaCalendarAlt, FaFilter, FaUsers } from 'react-icons/fa';

const AllAttendance = () => {
  const [attendance, setAttendance] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedUser, setSelectedUser] = useState('all');

  useEffect(() => {
    fetchUsers();
    fetchAttendance();
  }, []);

  useEffect(() => {
    fetchAttendance();
  }, [selectedDate, selectedUser]);

  const fetchUsers = async () => {
    try {
      const data = await userService.getAllUsers({ status: 'active' });
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      
      // Use the basic /attendance endpoint
      const response = await api.get('/attendance');
      
      if (response.data.success) {
        let allAttendance = response.data.attendance || [];
        
        console.log('All attendance records:', allAttendance);
        console.log('Selected date:', selectedDate);
        
        // Filter by selected date - comparing just the date part
        const filteredByDate = allAttendance.filter(record => {
          if (!record.date) return false;
          
          // Get date strings in YYYY-MM-DD format
          const recordDateStr = new Date(record.date).toISOString().split('T')[0];
          const selectedDateStr = selectedDate;
          
          console.log('Comparing:', recordDateStr, '===', selectedDateStr);
          
          return recordDateStr === selectedDateStr;
        });
        
        console.log('Filtered by date:', filteredByDate);
        
        // Filter by selected user if not 'all'
        const filteredData = selectedUser === 'all' 
          ? filteredByDate 
          : filteredByDate.filter(record => {
              const userId = record.user?._id || record.user;
              return userId === selectedUser;
            });
        
        console.log('Final filtered data:', filteredData);
        
        setAttendance(filteredData);
      } else {
        setAttendance([]);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      toast.error('Failed to load attendance data');
      setAttendance([]);
    } finally {
      setLoading(false);
    }
  };

  const getPresentCount = () => {
    return attendance.filter(a => a.status === 'present' || a.status === 'late').length;
  };

  const getAbsentCount = () => {
    if (selectedUser !== 'all') {
      return attendance.length === 0 ? 1 : 0;
    }
    return users.length - getPresentCount();
  };

  const getLateCount = () => {
    return attendance.filter(a => a.status === 'late').length;
  };

  const getTotalEmployees = () => {
    return selectedUser !== 'all' ? 1 : users.length;
  };

  const getAttendanceRate = () => {
    const total = getTotalEmployees();
    if (total === 0) return 0;
    return ((getPresentCount() / total) * 100).toFixed(1);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <FaUsers className="text-blue-600" />
          Team Attendance
        </h1>
        <p className="text-gray-600 mt-2">View attendance records for all employees</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaCalendarAlt className="inline mr-2" />
              Select Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Viewing: {new Date(selectedDate).toLocaleDateString()}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaFilter className="inline mr-2" />
              Filter by Employee
            </label>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Employees ({users.length})</option>
              {users.map(user => (
                <option key={user._id} value={user._id}>
                  {user.name} - {user.employeeId || 'N/A'}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Employees</p>
              <p className="text-4xl font-bold mt-2">{getTotalEmployees()}</p>
            </div>
            <FaUsers className="text-5xl text-blue-300 opacity-50" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Present</p>
              <p className="text-4xl font-bold mt-2">{getPresentCount()}</p>
            </div>
            <div className="text-5xl text-green-300 opacity-50">✓</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm font-medium">Late</p>
              <p className="text-4xl font-bold mt-2">{getLateCount()}</p>
            </div>
            <div className="text-5xl text-yellow-300 opacity-50">⏰</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm font-medium">Absent</p>
              <p className="text-4xl font-bold mt-2">{getAbsentCount()}</p>
            </div>
            <div className="text-5xl text-red-300 opacity-50">✗</div>
          </div>
        </div>
      </div>

      {/* Attendance Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-800">
            Attendance Records for {new Date(selectedDate).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </h2>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600"></div>
            <p className="mt-4 text-gray-600 font-medium">Loading attendance records...</p>
          </div>
        ) : attendance.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <div className="text-6xl mb-4">📅</div>
            <p className="text-xl font-medium text-gray-700 mb-2">No Attendance Records Found</p>
            <p className="text-sm text-gray-500 mb-4">
              No one has marked attendance for {new Date(selectedDate).toLocaleDateString()}
            </p>
            {selectedUser !== 'all' && (
              <p className="text-sm text-blue-600">
                Selected employee has not checked in on this date
              </p>
            )}
            <button
              onClick={fetchAttendance}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Refresh
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Check In
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Check Out
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hours Worked
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {attendance.map((record) => (
                  <tr key={record._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">
                            {record.user?.name?.charAt(0) || 'U'}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {record.user?.name || 'Unknown User'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {record.user?.employeeId || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.user?.department?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {record.checkIn ? new Date(record.checkIn).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {record.checkOut ? new Date(record.checkOut).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : (
                        <span className="text-yellow-600">Not checked out</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">
                      {record.totalHours ? `${record.totalHours.toFixed(2)} hrs` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        record.status === 'present' ? 'bg-green-100 text-green-800' :
                        record.status === 'late' ? 'bg-yellow-100 text-yellow-800' :
                        record.status === 'absent' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {record.status?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Attendance Percentage */}
      {!loading && users.length > 0 && (
        <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">Attendance Summary</h3>
              <p className="text-sm text-gray-600">
                {getPresentCount()} out of {getTotalEmployees()} employees marked attendance
              </p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-blue-600">
                {getAttendanceRate()}%
              </div>
              <p className="text-sm text-gray-600 mt-1">Attendance Rate</p>
            </div>
          </div>
        </div>
      )}

      {/* Debug Info - Remove in production */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-6 bg-gray-100 border border-gray-300 rounded-lg p-4">
          <h3 className="font-bold mb-2">Debug Info:</h3>
          <p className="text-sm">Selected Date: {selectedDate}</p>
          <p className="text-sm">Total Records: {attendance.length}</p>
          <p className="text-sm">Present: {getPresentCount()}</p>
          <p className="text-sm">
            Click browser console (F12) to see detailed filtering logs
          </p>
        </div>
      )}
    </div>
  );
};

export default AllAttendance;